import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getCreditConfiguration,
  createCreditConfiguration,
} from '@/api/credit';
import { Card, CardContent,  CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { Settings, Coins } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';

export default function CreditManagement() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Fetch credit configuration
  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['credit-config'],
    queryFn: getCreditConfiguration
  });

  // This page should only manage credit configuration and should not fetch cabinet quotas.

  // Update credit configuration
  const configMutation = useMutation({
    mutationFn: createCreditConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-config'] });
      toast({
        title: t('configuration_updated'),
        description: t('credit_config_description')
      });
    },
    onError: () => {
      toast({
        title: t('error'),
        description: t('failed_to_update'),
        variant: 'destructive'
      });
    }
  });

  const [inputCost, setInputCost] = useState(config?.input_token_cost || '0.000125');
  const [outputCost, setOutputCost] = useState(config?.output_token_cost || '0.000500');

  const handleUpdateConfig = () => {
    configMutation.mutate({
      input_token_cost: parseFloat(inputCost),
      output_token_cost: parseFloat(outputCost)
    });
  };

  if (configLoading) {
    return <div className="p-8">{t('loading')}</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        leading={
          <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Coins className="h-5 w-5" />
          </span>
        }
        title={t('credit_management')}
        description={t('manage_cabinet_quotas_and_usage_limits')}
      />

      {/* Credit Configuration Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              <CardTitle>{t('credit_configuration')}</CardTitle>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  {t('update_configuration')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('update_configuration')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>{t('input_token_cost')}</Label>
                    <Input
                      type="number"
                      step="0.000001"
                      value={inputCost}
                      onChange={(e) => setInputCost(e.target.value)}
                      placeholder="0.000125"
                    />
                    <p className="text-xs text-muted-foreground">
                      1 {t('credit_usage')} = {(1 / parseFloat(inputCost || '0.000125')).toFixed(0)} {t('input_tokens_credit')}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('output_token_cost')}</Label>
                    <Input
                      type="number"
                      step="0.000001"
                      value={outputCost}
                      onChange={(e) => setOutputCost(e.target.value)}
                      placeholder="0.000500"
                    />
                    <p className="text-xs text-muted-foreground">
                      1 {t('credit_usage')} = {(1 / parseFloat(outputCost || '0.000500')).toFixed(0)} {t('output_tokens_credit')}
                    </p>
                  </div>
                  <Button onClick={handleUpdateConfig} className="w-full">
                    {t('update_configuration')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">{t('input_token_cost')}</p>
              <p className="text-2xl font-bold">{config?.input_token_cost}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">{t('output_token_cost')}</p>
              <p className="text-2xl font-bold">{config?.output_token_cost}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* This page only displays credit configuration. Quotas listing moved out to dedicated pages. */}
    </div>
  );
}
